import {
  Box,
  Flex,
  Image,
  Progress,
  Text,
  Textarea,
  Input,
  Button,
  VStack,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Alert,
} from "@chakra-ui/react";
import { ChevronDownIcon, CloseIcon } from "@chakra-ui/icons";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { userSelector } from "store/user/selector";
import { axiosService } from "network/axios";
import UploadsIcon from "assets/img/support/uploads.svg";
import { initFilestackClient } from "network/filestack";
import { useDropzone } from "react-dropzone";
import { useSupportChats } from "../hooks";
import OrderIcon from "assets/img/support/order-icon.svg";
import PaymentsIcon from "assets/img/support/icon-payments.svg";
import RefundsIcon from "assets/img/support/refunds.svg";
import PermissionsIcon from "assets/img/support/permissions.svg";
import LockIcon from "assets/img/support/lock.svg";
import AnnotationIcon from "assets/img/support/Icon.svg";
import OtherIcon from "assets/img/support/annotation-question.svg";
import deleteIcon from "assets/img/customer/delete-icon.svg";
import fileIcon from "assets/img/customer/file-icon.svg";
import { SuccessCheckIcon } from "views/customer/profile/components/PersonalInformationCard";
import { ErrorAlert } from "views/auth/signIn/components/ErrorAlert";

interface UploadedFile {
  id: string;
  fileName: string;
  fileUrl: string;
  mimetype: string;
  size: number;
}

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

const ALLOWED_MIME_TYPES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/tiff",
]);

const ALLOWED_EXTENSIONS = new Set([
  "pdf",
  "doc",
  "docx",
  "png",
  "jpg",
  "jpeg",
  "tif",
  "tiff",
]);

const topicOptions = [
  { id: "order_support", label: "Order support", icon: OrderIcon },
  { id: "billing_payments", label: "Billing and payments", icon: PaymentsIcon },
  { id: "refunds", label: "Refunds", icon: RefundsIcon },
  {
    id: "team_permissions",
    label: "Team and permissions",
    icon: PermissionsIcon,
  },
  { id: "account_login", label: "Account and login", icon: LockIcon },
  {
    id: "new_translation",
    label: "New translation order",
    icon: AnnotationIcon,
  },
  { id: "other", label: "Other", icon: OtherIcon },
];

const ORDER_DISABLED_TOPICS = new Set([
  "team_permissions",
  "account_login",
  "new_translation",
]);

interface Project {
  order_id: number;
  project_title: string;
}

export default function NewRequestTab() {
  const user = useSelector(userSelector);
  const { createChat } = useSupportChats();
  const [selectedTopic, setSelectedTopic] = useState("");
  const [selectedOrder, setSelectedOrder] = useState("");
  const [message, setMessage] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [attachedFiles, setAttachedFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitValidationError, setIsSubmitValidationError] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<
    { id: string; name: string; progress: number }[]
  >([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isOrderDisabled = ORDER_DISABLED_TOPICS.has(selectedTopic);

  useEffect(() => {
    if (ORDER_DISABLED_TOPICS.has(selectedTopic)) {
      setSelectedOrder("");
    }
  }, [selectedTopic]);

  useEffect(() => {
    if (!submitSuccess) return;
    const t = window.setTimeout(() => setSubmitSuccess(false), 5000);
    return () => window.clearTimeout(t);
  }, [submitSuccess]);

  useEffect(() => {
    if (!submitError) return;
    const t = window.setTimeout(() => setSubmitError(null), 5000);
    return () => window.clearTimeout(t);
  }, [submitError]);

  useEffect(() => {
    if (submitError) return;
    if (!isSubmitValidationError) return;
    setIsSubmitValidationError(false);
  }, [submitError, isSubmitValidationError]);

  useEffect(() => {
    const fetchProjects = async () => {
      if (!user?.user_id) return;
      setIsLoadingProjects(true);
      try {
        const resp: any = await axiosService.get(
          `/projects/list/${user.user_id}?limit=100`,
        );
        const { results } = resp?.data;
        setProjects(results || []);
      } catch (err) {
        console.log(err);
        setProjects([]);
      } finally {
        setIsLoadingProjects(false);
      }
    };
    fetchProjects();
  }, [user?.user_id]);

  const validatePickerFiles = (filesUploaded: any[]) => {
    if (!filesUploaded || filesUploaded.length < 1) {
      return { ok: false, message: null as string | null };
    }

    for (const f of filesUploaded) {
      const size = Number(f?.size ?? 0);
      if (size > MAX_FILE_SIZE_BYTES) {
        return {
          ok: false,
          message: "Unsupported file type or size. Max 25 MB per file",
        };
      }

      const fileName = String(f?.filename ?? "");
      const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
      const mimetype = String(f?.mimetype ?? "");
      const typeOk = mimetype ? ALLOWED_MIME_TYPES.has(mimetype) : true;
      const extOk = ext ? ALLOWED_EXTENSIONS.has(ext) : true;
      if (!typeOk || !extOk) {
        return {
          ok: false,
          message: "Unsupported file type or size. Max 25 MB per file",
        };
      }
    }

    return { ok: true, message: null as string | null };
  };

  const handleFilestackUploadDone = (result: any) => {
    const filesUploaded = result?.filesUploaded ?? [];
    const { ok, message } = validatePickerFiles(filesUploaded);
    if (!ok) {
      setUploadError(message);
      setIsUploading(false);
      setUploadingFiles([]);
      return;
    }

    if (Array.isArray(filesUploaded) && filesUploaded.length > 0) {
      const newFiles: UploadedFile[] = filesUploaded.map((file: any) => ({
        id: String(file?.handle ?? file?.uploadId ?? file?.filename ?? "file"),
        fileName: String(file?.filename ?? "file"),
        fileUrl: String(file?.url ?? ""),
        mimetype: String(file?.mimetype ?? ""),
        size: Number(file?.size ?? 0),
      }));
      setAttachedFiles((prev) =>
        [...prev, ...newFiles].filter((f) => !!f?.fileUrl),
      );
    }

    setUploadingFiles((prev) => prev.map((it) => ({ ...it, progress: 100 })));
    setIsUploading(false);
    setUploadingFiles([]);
  };

  const openFilePicker = async () => {
    if (isUploading) return;

    setUploadError(null);
    const filestack = await initFilestackClient();
    filestack
      .picker({
        maxFiles: 10,
        maxSize: MAX_FILE_SIZE_BYTES,
        accept: [
          "image/*",
          ".pdf",
          ".doc",
          ".docx",
          ".xls",
          ".xlsx",
          ".txt",
          ".csv",
          ".json",
          ".tif",
          ".tiff",
        ],
        onFileUploadStarted: (file: any) => {
          setIsUploading(true);
          setUploadError(null);
          const name = String(file?.filename ?? "");
          const size = Number(file?.size ?? 0);
          const id = `${name}-${size}`;
          setUploadingFiles((prev) => {
            if (prev.some((it) => it.id === id)) return prev;
            return [...prev, { id, name, progress: 0 }];
          });
        },
        onFileUploadProgress: (file: any, progress: any) => {
          const name = String(file?.filename ?? "");
          const size = Number(file?.size ?? 0);
          const id = `${name}-${size}`;
          const next = Math.round(
            Number(progress?.totalPercent ?? progress ?? 0),
          );
          setUploadingFiles((prev) =>
            prev.map((it) =>
              it.id === id
                ? { ...it, progress: Math.min(100, Math.max(0, next)) }
                : it,
            ),
          );
        },
        onUploadDone: handleFilestackUploadDone,
        onClose: () => {
          setIsUploading(false);
          setUploadingFiles([]);
        },
      })
      .open();
  };

  const uploadFiles = async (files: File[]) => {
    setIsUploading(true);
    setUploadError(null);

    const filestack = await initFilestackClient();

    const pending = files.map((file) => ({
      id: `${file.name}-${file.size}-${file.lastModified}`,
      name: file.name,
      progress: 0,
    }));
    setUploadingFiles(pending);

    try {
      const uploaded = await Promise.all(
        files.map(async (file) => {
          const localId = `${file.name}-${file.size}-${file.lastModified}`;
          const res: any = await filestack.upload(file, {
            onProgress: (event: any) => {
              const totalPercent = Number(event?.totalPercent);
              const total = Number(event?.total ?? 0);
              const loaded = Number(event?.loaded ?? 0);
              const next = Number.isFinite(totalPercent)
                ? Math.round(totalPercent)
                : total > 0
                  ? Math.round((loaded / total) * 100)
                  : 0;
              setUploadingFiles((prev) =>
                prev.map((it) =>
                  it.id === localId
                    ? { ...it, progress: Math.min(100, Math.max(0, next)) }
                    : it,
                ),
              );
            },
          });
          return {
            id: String(res?.handle ?? localId),
            fileName: file.name,
            fileUrl: String(res?.url ?? ""),
            mimetype: String(file.type ?? ""),
            size: Number(file.size ?? 0),
          } as UploadedFile;
        }),
      );

      setUploadingFiles((prev) => prev.map((it) => ({ ...it, progress: 100 })));
      setAttachedFiles((prev) =>
        [...prev, ...uploaded].filter((f) => !!f?.fileUrl),
      );
    } catch (e) {
      setUploadError("Upload failed. Please try again.");
    } finally {
      setIsUploading(false);
      setUploadingFiles([]);
    }
  };

  const validateFiles = (files: File[]) => {
    if (files.length < 1) return { ok: false, message: null as string | null };

    for (const file of files) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        return {
          ok: false,
          message: "Unsupported file type or size. Max 25 MB per file",
        };
      }

      const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
      const typeOk = file.type ? ALLOWED_MIME_TYPES.has(file.type) : true;
      const extOk = ext ? ALLOWED_EXTENSIONS.has(ext) : true;
      if (!typeOk || !extOk) {
        return {
          ok: false,
          message: "Unsupported file type or size. Max 25 MB per file",
        };
      }
    }

    return { ok: true, message: null as string | null };
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: true,
    disabled: isUploading,
    noClick: true,
    noKeyboard: true,
    onDrop: (accepted) => {
      const v = validateFiles(accepted);
      if (!v.ok) {
        setUploadError(v.message);
        return;
      }
      uploadFiles(accepted);
    },
    onDropRejected: () => {
      setUploadError("Unsupported file type or size. Max 25 MB per file");
    },
  });

  const removeFile = (fileId: string) => {
    setAttachedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!selectedTopic || !message?.trim()) {
      setIsSubmitValidationError(true);
      setSubmitError(
        "Please select a topic and describe your request before submitting.",
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setIsSubmitting(true);
    setIsSubmitValidationError(false);
    setSubmitError(null);
    try {
      setSubmitSuccess(false);
      const attachments = attachedFiles.map((f) => f.fileUrl).filter(Boolean);
      const topic =
        topicOptions.find((t) => t.id === selectedTopic)?.label ??
        selectedTopic;
      await createChat({
        topic,
        message: message,
        attachments: attachments.length > 0 ? attachments : undefined,
        order_id: selectedOrder ? Number(selectedOrder) : undefined,
      });

      setMessage("");
      setContactEmail("");
      setAttachedFiles([]);
      setSelectedOrder("");
      setSubmitSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error: any) {
      setIsSubmitValidationError(false);
      const errorMessage =
        error?.data?.error || error?.error || "Failed to submit request";
      setSubmitError(errorMessage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      {/* Title block with grey background */}
      <Box
        bg="#F5F7FA"
        p={{ base: "16px", md: "23px" }}
        border="1px solid #E6ECF3"
        borderRadius="12px"
        mb={{ base: "16px", md: "0" }}
        display={{ base: "block", md: "none" }}
      >
        <Text fontSize="20px" fontWeight="600" color="#0B0E10">
          Submit a request
        </Text>
      </Box>

      {/* Desktop container with grey background */}
      <Box
        display={{ base: "none", md: "block" }}
        bg="#F5F7FA"
        p="23px"
        border="1px solid #E6ECF3"
        borderRadius="12px"
      >
        <Text fontSize="20px" fontWeight="600" color="#0B0E10" mb="16px">
          Submit a request
        </Text>

        {submitSuccess && (
          <Alert status="success" bg="#E9F7EF" mb="24px" borderRadius="8px">
            <SuccessCheckIcon ml="auto" mr="10px" boxSize="20px" />
            <Text fontSize="14px" fontWeight="400" color="#0B0E10" flex="1">
              Thank you! Your request has been submitted. Our support team will
              respond shortly.
            </Text>
          </Alert>
        )}

        {submitError && (
          <ErrorAlert
            icon
            message={submitError}
            bgColor={isSubmitValidationError ? "#FFF8E6" : undefined}
            iconColor={isSubmitValidationError ? "#B6660B" : undefined}
          />
        )}

        <Flex
          border="1px solid #E6ECF3"
          borderRadius="12px"
          p="23px"
          bg="#FFF"
          gap="24px"
          flexDirection={{ base: "column", lg: "row" }}
        >
          {/* Topic Selector */}
          <Box
            minW={{ base: "100%", lg: "265px" }}
            pr={{ base: "0", lg: "24px" }}
            borderRight={{ base: "none", lg: "1px solid #E6ECF3" }}
          >
            <Text fontSize="16px" fontWeight="600" color="#0B0E10" mb="12px">
              Topic Selector:
            </Text>
            <VStack spacing="8px" align="stretch">
              {topicOptions.map((topic) => (
                <Box
                  key={topic.id}
                  px="12px"
                  py="11px"
                  cursor="pointer"
                  bg={selectedTopic === topic.id ? "#EBF5FF" : "#FFFFFF"}
                  borderRadius="8px"
                  border={
                    selectedTopic === topic.id
                      ? "1px solid #2F80ED"
                      : "1px solid #E6ECF3"
                  }
                  onClick={() => setSelectedTopic(topic.id)}
                >
                  <Flex align="center" gap="12px">
                    <Box
                      as="span"
                      w="20px"
                      h="20px"
                      bg={selectedTopic === topic.id ? "#3A7BFF" : "#0B0E10"}
                      sx={{
                        mask: `url(${topic.icon}) no-repeat center / contain`,
                        WebkitMask: `url(${topic.icon}) no-repeat center / contain`,
                      }}
                    />
                    <Text
                      fontSize="14px"
                      fontWeight="400"
                      color={selectedTopic === topic.id ? "#2F80ED" : "#0B0E10"}
                    >
                      {topic.label}
                    </Text>
                  </Flex>
                </Box>
              ))}
            </VStack>
          </Box>

          {/* Form */}
          <Box flex="1">
            <Text fontSize="18px" fontWeight="600" color="#0B0E10" mb="24px">
              What do you need help with?
            </Text>

            {/* Order Select */}
            <Box mb="24px" opacity={isOrderDisabled ? 0.5 : 1}>
              <Flex align="baseline" gap="4px" mb="8px">
                <Text fontSize="14px" color="#0B0E10">
                  Order
                </Text>
                <Text fontSize="12px" color="#667789" fontWeight="400">
                  (optional)
                </Text>
              </Flex>
              <Menu matchWidth>
                <MenuButton
                  as={Button}
                  rightIcon={
                    <Flex align="center" gap="4px" flexShrink={0}>
                      {selectedOrder && !isOrderDisabled && (
                        <Box
                          as="button"
                          type="button"
                          onClick={(e: any) => {
                            e.stopPropagation();
                            setSelectedOrder("");
                          }}
                          aria-label="Clear selected order"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          w="20px"
                          h="20px"
                          borderRadius="4px"
                          // _hover={{ bg: "#E5E7EB" }}
                        >
                          <CloseIcon boxSize="12px" color="#6B7280" />
                        </Box>
                      )}
                      <ChevronDownIcon />
                    </Flex>
                  }
                  w="100%"
                  h="48px"
                  bg={isOrderDisabled ? "#F5F7FA" : "white"}
                  border="1px solid #E5E7EB"
                  borderRadius="8px"
                  fontSize="14px"
                  fontWeight="400"
                  textAlign="left"
                  isDisabled={isOrderDisabled || isLoadingProjects || projects.length === 0}
                  _hover={{ borderColor: isOrderDisabled ? "#E5E7EB" : "#D1D5DB" }}
                  _focus={{ borderColor: isOrderDisabled ? "#E5E7EB" : "#2F80ED" }}
                  _expanded={{ borderColor: "#2F80ED", bg: "#FFF" }}
                  _disabled={{ opacity: 1, cursor: "not-allowed" }}
                  sx={{
                    "& > span:first-of-type": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                      flex: 1,
                    },
                  }}
                >
                  <Text
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    maxW="100%"
                    fontSize="16px"
                    color={isOrderDisabled ? "#A0AEC0" : selectedOrder ? "#0B0E10" : "#667789"}
                  >
                    {isOrderDisabled
                      ? "Not applicable for this topic"
                      : isLoadingProjects
                        ? "Loading projects..."
                        : projects.length === 0
                          ? "You don't have any orders yet"
                          : selectedOrder
                            ? projects.find(
                                (p) => String(p.order_id) === selectedOrder,
                              )?.project_title || `Order #${selectedOrder}`
                            : "Choose the order you need help with"}
                  </Text>
                </MenuButton>
                {projects.length > 0 ? (
                  <MenuList maxH="300px" overflowY="auto">
                    {projects.map((project) => (
                      <MenuItem
                        key={project.order_id}
                        onClick={() =>
                          setSelectedOrder(String(project.order_id))
                        }
                        bg={
                          selectedOrder === String(project.order_id)
                            ? "#EBF5FF"
                            : "transparent"
                        }
                        _hover={{ bg: "#F3F4F6" }}
                      >
                        {project.project_title || `Order #${project.order_id}`}
                      </MenuItem>
                    ))}
                  </MenuList>
                ) : null}
              </Menu>
            </Box>

            {/* Message */}
            <Box mb="24px">
              <Text fontSize="14px" color="#0B0E10" mb="8px">
                Message
              </Text>
              <Textarea
                placeholder="Describe your request or question..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                bg="white"
                border="1px solid #D2DAE5"
                borderRadius="8px"
                fontSize="16px"
                minH="148px"
                resize="vertical"
                _hover={{}}
                _focus={{ border: "1px solid #2F80ED" }}
              />
            </Box>

            {/* File Upload */}
            <Box mb="20px">
              <Text fontSize="14px" color="#0B0E10" mb="8px">
                Attach File (optional)
              </Text>

              {uploadError ? (
                <Box
                  mt="12px"
                  p="12px"
                  borderRadius="12px"
                  bg="#FDECEC"
                  color="#000000"
                  fontSize="14px"
                >
                  {uploadError}
                </Box>
              ) : null}

              <Box
                {...getRootProps({
                  onClick: () => {
                    openFilePicker();
                  },
                })}
                bg="#FFFFFF"
                border="1px dashed #7C8FA3"
                borderRadius="8px"
                p="24px"
                textAlign="center"
                cursor={isUploading ? "not-allowed" : "pointer"}
                opacity={isUploading ? 0.7 : 1}
              >
                <input {...getInputProps()} />
                <Flex justify="center" mb="12px">
                  <Image src={UploadsIcon} w="20px" h="20px" alt="Upload" />
                </Flex>
                <Text fontSize="14px" color="#6B7280">
                  <Text as="span" color="#2F80ED" fontWeight="500">
                    {isUploading ? "Uploading..." : "Click to upload"}
                  </Text>{" "}
                  <Text
                    as="span"
                    color="#0B0E10"
                    fontSize="14px"
                    fontWeight="400"
                  >
                    {isDragActive ? "(drop now)" : "or drag and drop"}
                  </Text>
                </Text>
                <Text fontSize="12px" color="#0B0E10">
                  SVG, PNG, JPG or GIF (max. 800x400px).
                </Text>
              </Box>

              {uploadingFiles.length > 0 ? (
                <Box mt="12px">
                  {uploadingFiles.map((f) => (
                    <Box
                      key={f.id}
                      p="12px"
                      bg="#FFF"
                      borderRadius="12px"
                      border="1px solid #D2DAE5"
                      mb="10px"
                    >
                      <Flex
                        justifyContent="space-between"
                        alignItems="center"
                        mb="8px"
                      >
                        <Flex minW="0" alignItems="center" gap="8px">
                          <Image
                            src={fileIcon}
                            alt="file"
                            boxSize="20px"
                            flexShrink={0}
                          />
                          <Text
                            fontSize="14px"
                            fontWeight="500"
                            color="#0B0E10"
                            noOfLines={1}
                          >
                            {f.name}
                          </Text>
                        </Flex>
                        <Text fontSize="12px" color="#667085" flexShrink={0}>
                          {f.progress >= 100 ? "Processing" : `${f.progress}%`}
                        </Text>
                      </Flex>
                      <Progress
                        value={Math.min(100, Math.max(0, f.progress))}
                        isIndeterminate={f.progress >= 100 && isUploading}
                        size="sm"
                        borderRadius="999px"
                        colorScheme="blue"
                        bg="#EEF2F6"
                      />
                    </Box>
                  ))}
                </Box>
              ) : null}

              {attachedFiles.length > 0 ? (
                <Box mt="12px" maxH="180px" overflowY="auto">
                  {attachedFiles.map((file) => (
                    <Flex
                      key={file.id}
                      align="center"
                      justify="space-between"
                      p="10px"
                      bg="#FFF"
                      borderRadius="12px"
                      border="1px solid #D2DAE5"
                      mb="10px"
                    >
                      <Flex minW="0" alignItems="center" gap="8px">
                        <Image
                          src={fileIcon}
                          alt="file"
                          boxSize="20px"
                          flexShrink={0}
                        />
                        <Text
                          fontSize="14px"
                          fontWeight="500"
                          color="#0B0E10"
                          noOfLines={1}
                        >
                          {file.fileName}
                        </Text>
                      </Flex>
                      <IconButton
                        aria-label="Remove file"
                        variant="ghost"
                        size="sm"
                        color="#2F80ED"
                        _hover={{ bg: "transparent" }}
                        _active={{ bg: "transparent" }}
                        _focus={{ boxShadow: "none" }}
                        icon={
                          <Image src={deleteIcon} alt="delete" boxSize="20px" />
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(file.id);
                        }}
                      />
                    </Flex>
                  ))}
                </Box>
              ) : null}
              <Text fontSize="12px" color="#0B0E10" mt="8px">
                Attach documents or screenshots related to your request so we
                can assist you faster.
              </Text>
            </Box>

            {/* Contact Email */}
            <Box mb="24px">
              <Flex align="baseline" gap="4px" mb="8px">
                <Text fontSize="14px" color="#0B0E10">
                  Contact email
                </Text>
                <Text fontSize="12px" color="#667789" fontWeight="400">
                  (optional)
                </Text>
              </Flex>
              <Input
                type="email"
                placeholder="Johndoe@gmail.com"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                bg="white"
                border="1px solid #E5E7EB"
                borderRadius="8px"
                fontSize="16px"
                h="44px"
                _hover={{}}
                _focus={{ border: "1px solid #2F80ED" }}
              />
            </Box>

            {/* Submit Button */}
            <Button
              type="button"
              w="100%"
              h="48px"
              bgColor="#3A7BFF"
              color="#FFFFFF"
              fontSize="16px"
              fontWeight="600"
              borderRadius="25px"
              border="1px solid #2C63E5"
              _hover={{
                bgColor: "#FFFFFF",
                color: "#2C63E5",
              }}
              _active={{
                bgColor: "#FFFFFF",
                color: "#2C63E5",
              }}
              _loading={{ pointerEvents: "none" }}
              onClick={handleSubmit}
              isLoading={isSubmitting}
              isDisabled={false}
            >
              Send request
            </Button>
          </Box>
        </Flex>
      </Box>

      {/* Mobile content - full width without container */}
      <Box display={{ base: "block", md: "none" }}>
        {submitSuccess && (
          <Alert status="success" bg="#E9F7EF" mb="16px" borderRadius="8px">
            <SuccessCheckIcon ml="auto" mr="10px" boxSize="20px" />
            <Text fontSize="14px" fontWeight="400" color="#0B0E10" flex="1">
              Thank you! Your request has been submitted. Our support team will
              respond shortly.
            </Text>
          </Alert>
        )}

        {submitError && (
          <ErrorAlert
            icon
            message={submitError}
            bgColor={isSubmitValidationError ? "#FFF8E6" : undefined}
            iconColor={isSubmitValidationError ? "#B6660B" : undefined}
          />
        )}

        {/* Topic Selector */}
        <Box mb="24px">
          <Text fontSize="16px" fontWeight="600" color="#0B0E10" mb="12px">
            Topic Selector:
          </Text>
          <VStack spacing="8px" align="stretch">
            {topicOptions.map((topic) => (
              <Box
                key={topic.id}
                px="12px"
                py="11px"
                cursor="pointer"
                bg={selectedTopic === topic.id ? "#EBF5FF" : "#FFFFFF"}
                borderRadius="8px"
                border={
                  selectedTopic === topic.id
                    ? "1px solid #2F80ED"
                    : "1px solid #E6ECF3"
                }
                onClick={() => setSelectedTopic(topic.id)}
              >
                <Flex align="center" gap="12px">
                  <Box
                    as="span"
                    w="20px"
                    h="20px"
                    bg={selectedTopic === topic.id ? "#3A7BFF" : "#0B0E10"}
                    sx={{
                      mask: `url(${topic.icon}) no-repeat center / contain`,
                      WebkitMask: `url(${topic.icon}) no-repeat center / contain`,
                    }}
                  />
                  <Text
                    fontSize="14px"
                    fontWeight="400"
                    color={selectedTopic === topic.id ? "#2F80ED" : "#0B0E10"}
                  >
                    {topic.label}
                  </Text>
                </Flex>
              </Box>
            ))}
          </VStack>
        </Box>

        {/* Form */}
        <Box>
          <Text fontSize="18px" fontWeight="600" color="#0B0E10" mb="24px">
            What do you need help with?
          </Text>

          {/* Order Select */}
          <Box mb="24px" opacity={isOrderDisabled ? 0.5 : 1}>
            <Flex align="baseline" gap="4px" mb="8px">
              <Text fontSize="14px" color="#0B0E10">
                Order
              </Text>
              <Text fontSize="12px" color="#667789" fontWeight="400">
                (optional)
              </Text>
            </Flex>
            <Menu matchWidth>
              <MenuButton
                as={Button}
                rightIcon={
                  <Flex align="center" gap="4px" flexShrink={0}>
                    {selectedOrder && !isOrderDisabled && (
                      <Box
                        as="button"
                        type="button"
                        onClick={(e: any) => {
                          e.stopPropagation();
                          setSelectedOrder("");
                        }}
                        aria-label="Clear selected order"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        w="20px"
                        h="20px"
                        borderRadius="4px"
                      >
                        <CloseIcon boxSize="12px" color="#6B7280" />
                      </Box>
                    )}
                    <ChevronDownIcon />
                  </Flex>
                }
                w="100%"
                h="48px"
                bg={isOrderDisabled ? "#F5F7FA" : "white"}
                border="1px solid #E5E7EB"
                borderRadius="8px"
                fontSize="14px"
                fontWeight="400"
                textAlign="left"
                isDisabled={isOrderDisabled || isLoadingProjects || projects.length === 0}
                _hover={{ borderColor: isOrderDisabled ? "#E5E7EB" : "#D1D5DB" }}
                _focus={{ borderColor: isOrderDisabled ? "#E5E7EB" : "#2F80ED" }}
                _expanded={{ borderColor: "#2F80ED", bg: "#FFF" }}
                _disabled={{ opacity: 1, cursor: "not-allowed" }}
                sx={{
                  "& > span:first-of-type": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                    flex: 1,
                  },
                }}
              >
                <Text
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  maxW="100%"
                  fontSize="16px"
                  color={isOrderDisabled ? "#A0AEC0" : selectedOrder ? "#0B0E10" : "#667789"}
                >
                  {isOrderDisabled
                    ? "Not applicable for this topic"
                    : isLoadingProjects
                      ? "Loading projects..."
                      : projects.length === 0
                        ? "You don't have any orders yet"
                        : selectedOrder
                          ? projects.find(
                              (p) => String(p.order_id) === selectedOrder,
                            )?.project_title || `Order #${selectedOrder}`
                          : "Choose the order you need help with"}
                </Text>
              </MenuButton>
              {projects.length > 0 ? (
                <MenuList maxH="300px" overflowY="auto">
                  {projects.map((project) => (
                    <MenuItem
                      key={project.order_id}
                      onClick={() => setSelectedOrder(String(project.order_id))}
                    >
                      {project.project_title || `Order #${project.order_id}`}
                    </MenuItem>
                  ))}
                </MenuList>
              ) : null}
            </Menu>
          </Box>

          {/* Message */}
          <Box mb="24px">
            <Text fontSize="14px" color="#0B0E10" mb="8px">
              Message
            </Text>
            <Textarea
              placeholder="Describe your request or question..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              bg="white"
              border="1px solid #D2DAE5"
              borderRadius="8px"
              fontSize="16px"
              minH="148px"
              resize="vertical"
              _hover={{}}
              _focus={{ border: "1px solid #2F80ED" }}
            />
          </Box>

          {/* File Upload */}
          <Box mb="20px">
            <Text fontSize="14px" color="#0B0E10" mb="8px">
              Attach File (optional)
            </Text>

            {uploadError ? (
              <Box
                mt="12px"
                p="12px"
                borderRadius="12px"
                bg="#FDECEC"
                color="#000000"
                fontSize="14px"
              >
                {uploadError}
              </Box>
            ) : null}

            <Box
              {...getRootProps({
                onClick: () => {
                  openFilePicker();
                },
              })}
              bg="#FFFFFF"
              border="1px dashed #7C8FA3"
              borderRadius="8px"
              p="24px"
              textAlign="center"
              cursor={isUploading ? "not-allowed" : "pointer"}
              opacity={isUploading ? 0.7 : 1}
            >
              <input {...getInputProps()} />
              <Flex justify="center" mb="12px">
                <Image src={UploadsIcon} w="20px" h="20px" alt="Upload" />
              </Flex>
              <Text fontSize="14px" color="#6B7280">
                <Text as="span" color="#2F80ED" fontWeight="500">
                  {isUploading ? "Uploading..." : "Click to upload"}
                </Text>{" "}
                <Text
                  as="span"
                  color="#0B0E10"
                  fontSize="14px"
                  fontWeight="400"
                >
                  {isDragActive ? "(drop now)" : "or drag and drop"}
                </Text>
              </Text>
              <Text fontSize="12px" color="#0B0E10">
                SVG, PNG, JPG or GIF (max. 800x400px).
              </Text>
            </Box>

            {uploadingFiles.length > 0 ? (
              <Box mt="12px">
                {uploadingFiles.map((f) => (
                  <Box
                    key={f.id}
                    p="12px"
                    bg="#FFF"
                    borderRadius="12px"
                    border="1px solid #D2DAE5"
                    mb="10px"
                  >
                    <Flex
                      justifyContent="space-between"
                      alignItems="center"
                      mb="8px"
                    >
                      <Flex minW="0" alignItems="center" gap="8px">
                        <Image
                          src={fileIcon}
                          alt="file"
                          boxSize="20px"
                          flexShrink={0}
                        />
                        <Text
                          fontSize="14px"
                          fontWeight="500"
                          color="#0B0E10"
                          noOfLines={1}
                        >
                          {f.name}
                        </Text>
                      </Flex>
                      <Text fontSize="12px" color="#667085" flexShrink={0}>
                        {f.progress >= 100 ? "Processing" : `${f.progress}%`}
                      </Text>
                    </Flex>
                    <Box
                      h="6px"
                      bg="#E5E7EB"
                      borderRadius="3px"
                      overflow="hidden"
                    >
                      <Box
                        h="100%"
                        w={`${f.progress}%`}
                        bg="#2F80ED"
                        borderRadius="3px"
                        transition="width 0.2s"
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : null}

            {attachedFiles.length > 0 ? (
              <Box mt="12px">
                {attachedFiles.map((file) => (
                  <Flex
                    key={file.id}
                    align="center"
                    justify="space-between"
                    p="10px"
                    bg="#FFF"
                    borderRadius="12px"
                    border="1px solid #D2DAE5"
                    mb="10px"
                  >
                    <Flex minW="0" alignItems="center" gap="8px">
                      <Image
                        src={fileIcon}
                        alt="file"
                        boxSize="20px"
                        flexShrink={0}
                      />
                      <Text
                        fontSize="14px"
                        fontWeight="500"
                        color="#0B0E10"
                        noOfLines={1}
                      >
                        {file.fileName}
                      </Text>
                    </Flex>
                    <IconButton
                      aria-label="Remove file"
                      variant="ghost"
                      size="sm"
                      color="#2F80ED"
                      _hover={{ bg: "transparent" }}
                      _active={{ bg: "transparent" }}
                      _focus={{ boxShadow: "none" }}
                      icon={
                        <Image src={deleteIcon} alt="delete" boxSize="20px" />
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(file.id);
                      }}
                    />
                  </Flex>
                ))}
              </Box>
            ) : null}
          </Box>

          {/* Contact Email */}
          <Box mb="20px">
            <Flex align="baseline" gap="4px" mb="8px">
              <Text fontSize="14px" color="#0B0E10">
                Contact email
              </Text>
              <Text fontSize="12px" color="#667789" fontWeight="400">
                (optional)
              </Text>
            </Flex>
            <Input
              type="email"
              placeholder="Johndoe@gmail.com"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              bg="white"
              border="1px solid #E5E7EB"
              borderRadius="8px"
              fontSize="16px"
              h="44px"
              _hover={{}}
              _focus={{ border: "1px solid #2F80ED" }}
            />
          </Box>

          {/* Submit Button */}
          <Button
            type="button"
            w="100%"
            h="48px"
            bgColor="#3A7BFF"
            color="#FFFFFF"
            fontSize="16px"
            fontWeight="600"
            borderRadius="25px"
            border="1px solid #2C63E5"
            _hover={{
              bgColor: "#FFFFFF",
              color: "#2C63E5",
            }}
            _active={{
              bgColor: "#FFFFFF",
              color: "#2C63E5",
            }}
            _loading={{ pointerEvents: "none" }}
            onClick={handleSubmit}
            isLoading={isSubmitting}
            isDisabled={false}
          >
            Send request
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
